import PropTypes from "prop-types";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import useMediaQuery from "@mui/material/useMediaQuery";

import MainCard from "components/MainCard";
import { useSurfaceTheme } from "utils/surfaceTheme";

export default function MobileWorkspaceScreen({
  title,
  secondary,
  intro,
  children,
  contentSX,
  sx,
  mobileFillViewport = false,
}) {
  const downMD = useMediaQuery((theme) => theme.breakpoints.down("md"));
  const { panelBorderColor } = useSurfaceTheme("panel");
  const fillMobile = downMD && mobileFillViewport;
  const fillLayout = fillMobile || !downMD;
  const desktopToolbar = !downMD && secondary ? secondary : null;
  const mobileToolbar = downMD && secondary ? secondary : null;

  return (
    <MainCard
      sx={{
        height: fillLayout ? "100%" : downMD ? "auto" : "100%",
        minHeight: fillLayout ? 0 : downMD ? "auto" : 0,
        display: fillLayout ? "flex" : downMD ? undefined : "flex",
        flexDirection: fillLayout ? "column" : downMD ? undefined : "column",
        overflow: fillLayout ? "hidden" : downMD ? "visible" : "hidden",
        border: downMD ? "none" : undefined,
        borderRadius: downMD ? 0 : 1,
        boxShadow: "none",
        ...(fillLayout
          ? {
              /* Only MainCard shell — do not zero padding on nested MobileDataCard content. */
              "& > .MuiCardContent-root": {
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              },
            }
          : {}),
        "& > .MuiCardContent-root": {
          px: downMD ? 0 : undefined,
          pt: downMD ? 0.5 : undefined,
        },
        ...(sx || {}),
      }}
      contentSX={{
        height: fillLayout ? "100%" : downMD ? "auto" : "100%",
        minHeight: fillLayout ? 0 : downMD ? "auto" : 0,
        display: "flex",
        flexDirection: "column",
        overflow: fillLayout ? "hidden" : undefined,
        px: downMD ? 0 : 1.25,
        pt: downMD ? 0.25 : 0.5,
        pb: downMD ? 0 : 1.25,
        ...(contentSX || {}),
      }}
      aria-label={typeof title === "string" ? title : undefined}
    >
      <Stack
        spacing={0.75}
        sx={{
          flex: fillMobile || !downMD ? 1 : "0 0 auto",
          minHeight: fillMobile || !downMD ? 0 : "auto",
          overflow: fillMobile ? "hidden" : undefined,
        }}
      >
        {desktopToolbar ? (
          <Box
            sx={{
              flexShrink: 0,
              pb: 0.5,
              borderBottom: 1,
              borderColor: panelBorderColor,
            }}
          >
            {desktopToolbar}
          </Box>
        ) : null}
        {mobileToolbar ? (
          <Box sx={{ flexShrink: 0, px: 1, pb: 0.5 }}>{mobileToolbar}</Box>
        ) : null}
        {intro ? (
          <Box sx={{ flexShrink: 0, px: { xs: 1, md: 0 } }}>{intro}</Box>
        ) : null}
        <Box
          sx={{
            flex: fillMobile || !downMD ? 1 : "0 0 auto",
            minHeight: fillMobile || !downMD ? 0 : "auto",
            overflow: fillMobile || !downMD ? "hidden" : "visible",
            display: fillMobile || !downMD ? "flex" : undefined,
            flexDirection: fillMobile || !downMD ? "column" : undefined,
          }}
        >
          {children}
        </Box>
      </Stack>
    </MainCard>
  );
}

MobileWorkspaceScreen.propTypes = {
  /** Used for accessibility only; page title is shown in the app bar. */
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  secondary: PropTypes.node,
  intro: PropTypes.node,
  children: PropTypes.node,
  contentSX: PropTypes.object,
  sx: PropTypes.object,
  mobileFillViewport: PropTypes.bool,
};
